import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";
import type { TablesInsert, TablesUpdate } from "~/api/tables";
import { db } from "~/api/db";
import {
  normalizeBrandAccentHex,
  timeRoundingMethod,
} from "~/utils/time-rounding";
import {
  invoiceRoundingMethod,
  requireText,
  rootKey,
  textOrNull,
  type Workspace,
  type WorkspaceMember,
} from "./shared";

async function selectAccessibleWorkspaces(user: {
  id: string;
  name?: string | null;
  email?: string | null;
}) {
  const { data, error } = await db
    .from("workspaces")
    .select("*")
    .order("created_at", { ascending: true })
    .order("name", { ascending: true });

  if (error) throw error;
  if (data?.length) return data as Workspace[];

  const fallbackName =
    user.name?.trim() || user.email?.split("@")[0] || "MintStint";
  const { data: created, error: createError } = await db
    .from("workspaces")
    .insert({
      owner_id: user.id,
      name: `${fallbackName}'s workspace`,
    } satisfies TablesInsert<"workspaces">)
    .select("*")
    .single();

  if (createError) throw createError;
  return [created as Workspace];
}

function activeWorkspaceId(user: ReturnType<typeof useAuth>["user"]) {
  const value = user?.preferences?.activeWorkspaceId;
  return typeof value === "string" ? value : null;
}

function chooseActiveWorkspace(
  workspaces: Workspace[] | undefined,
  user: ReturnType<typeof useAuth>["user"],
) {
  if (!workspaces?.length || !user) return undefined;

  const selectedId = activeWorkspaceId(user);
  return (
    workspaces.find((workspace) => workspace.id === selectedId) ??
    workspaces.find((workspace) => workspace.owner_id === user.id) ??
    workspaces[0]
  );
}

function useAccessibleWorkspaces() {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...rootKey, "directory", user?.id ?? ""],
    queryFn: () => {
      if (!user) throw new Error("Sign in to load your workspaces.");
      return selectAccessibleWorkspaces(user);
    },
    enabled: Boolean(user),
  });
}

export function usePrimaryWorkspace() {
  const { user } = useAuth();
  const directory = useAccessibleWorkspaces();

  return {
    ...directory,
    data: chooseActiveWorkspace(directory.data, user),
  };
}

export function useWorkspaceSwitcher() {
  const { user, updatePreferences } = useAuth();
  const directory = useAccessibleWorkspaces();
  const selectedWorkspace = chooseActiveWorkspace(directory.data, user);
  const selectWorkspace = useMutation({
    mutationFn: async (workspaceId: string) => {
      const workspace = directory.data?.find(
        (candidate) => candidate.id === workspaceId,
      );
      if (!workspace) throw new Error("That workspace is not available.");
      await updatePreferences({ activeWorkspaceId: workspace.id });
      return workspace;
    },
  });

  return {
    workspaces: directory.data ?? [],
    selectedWorkspace,
    isLoading: directory.isLoading,
    error: directory.error ?? selectWorkspace.error,
    selectWorkspace,
  };
}

export function useWorkspaceMembership(workspaceId: string | undefined) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...rootKey, "membership", workspaceId ?? "", user?.id ?? ""],
    queryFn: async () => {
      if (!workspaceId || !user) return null as WorkspaceMember | null;
      const { data, error } = await db
        .from("workspace_members")
        .select("*")
        .eq("workspace_id", workspaceId)
        .eq("user_id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data as WorkspaceMember | null;
    },
    enabled: Boolean(workspaceId && user),
  });
}

export function useWorkspaceSettings(workspaceId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Record<string, string | boolean>) => {
      if (!workspaceId) throw new Error("Workspace is still loading.");
      const defaultRate = Number(input.defaultHourlyRate);
      const setAsidePercentage = Number(input.taxSetAsidePercentage);
      const invoicePrefix = String(input.invoicePrefix ?? "").trim();
      const patch: TablesUpdate<"workspaces"> = {
        address: textOrNull(String(input.address ?? "")),
        bank_account_name: textOrNull(String(input.bankAccountName ?? "")),
        bank_account_number: textOrNull(String(input.bankAccountNumber ?? "")),
        bank_name: textOrNull(String(input.bankName ?? "")),
        brand_accent_hex: normalizeBrandAccentHex(
          String(input.brandAccentHex ?? ""),
        ),
        contact_email: textOrNull(String(input.contactEmail ?? "")),
        default_hourly_rate_cents:
          Number.isFinite(defaultRate) && defaultRate >= 0
            ? Math.round(defaultRate * 100)
            : 0,
        default_invoice_rounding: invoiceRoundingMethod(
          String(input.defaultInvoiceRounding ?? ""),
        ),
        default_payment_instructions: textOrNull(
          String(input.defaultPaymentInstructions ?? ""),
        ),
        default_time_rounding: timeRoundingMethod(
          String(input.defaultTimeRounding ?? ""),
        ),
        gst_number: textOrNull(String(input.gstNumber ?? "")),
        gst_registered: Boolean(input.gstRegistered),
        ird_number: textOrNull(String(input.irdNumber ?? "")),
        idle_nudge_minutes: (() => {
          const value = Number(input.idleNudgeMinutes);
          if (!Number.isFinite(value) || value < 0) return 15;
          return Math.min(240, Math.round(value));
        })(),
        focus_reminder_minutes: (() => {
          const value = Number(input.focusReminderMinutes);
          if (!Number.isFinite(value) || value < 0) return 0;
          return Math.min(240, Math.round(value));
        })(),
        invoice_email_subject_template: textOrNull(
          String(input.invoiceEmailSubjectTemplate ?? ""),
        ),
        invoice_email_body_template: textOrNull(
          String(input.invoiceEmailBodyTemplate ?? ""),
        ),
        quote_email_subject_template: textOrNull(
          String(input.quoteEmailSubjectTemplate ?? ""),
        ),
        quote_email_body_template: textOrNull(
          String(input.quoteEmailBodyTemplate ?? ""),
        ),
        invoice_prefix: invoicePrefix || "MS",
        legal_name: textOrNull(String(input.legalName ?? "")),
        name: requireText(String(input.name ?? ""), "Workspace name"),
        nzbn: textOrNull(String(input.nzbn ?? "")),
        phone: textOrNull(String(input.phone ?? "")),
        tax_set_aside_percentage:
          Number.isFinite(setAsidePercentage) && setAsidePercentage >= 0
            ? Math.min(setAsidePercentage, 100)
            : 0,
      };
      if (typeof input.logoPath === "string") {
        patch.logo_path = input.logoPath.trim() || null;
      }
      const { error } = await db
        .from("workspaces")
        .update(patch)
        .eq("id", workspaceId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rootKey });
    },
  });
}
